import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Pickaxe, Users, Zap, Coins, Lock, TrendingUp, UserPlus, Check, X, Bell } from "lucide-react";
import { toast } from "sonner";

const MinePage = () => {
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const api = authAxios();

  const [status, setStatus] = useState(null);
  const [invites, setInvites] = useState({ incoming: [], outgoing: [], members: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [invitePhone, setInvitePhone] = useState("");

  // Local high-frequency display so the LTG counter ticks every second smoothly
  const [displayAccrued, setDisplayAccrued] = useState(0);
  const lastSyncRef = useRef({ t: 0, accrued: 0, ratePerSec: 0 });

  const fetchAll = async () => {
    try {
      const [s, i] = await Promise.all([
        api.get("/mining/status"),
        api.get("/mining/invites"),
      ]);
      setStatus(s.data);
      setInvites(i.data);
      lastSyncRef.current = {
        t: Date.now(),
        accrued: s.data.accrued_ltg || 0,
        ratePerSec: s.data.session_rate_per_sec || 0,
      };
      setDisplayAccrued(s.data.accrued_ltg || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // Auto-clear unread reminders on mount (so dashboard badge goes away)
  useEffect(() => {
    api.post("/mining/reminders/clear").catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  // Smooth per-second tick (interpolate locally)
  useEffect(() => {
    if (!status?.active) return;
    const tick = () => {
      const { t, accrued, ratePerSec } = lastSyncRef.current;
      const elapsed = (Date.now() - t) / 1000;
      const live = accrued + elapsed * ratePerSec;
      const cap = status.session_rate_per_day || 0;
      setDisplayAccrued(Math.min(live, cap));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [status]);

  const handleStart = async () => {
    setBusy(true);
    try {
      const res = await api.post("/mining/start");
      toast.success(res.data.message || "Mining started!");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
    setBusy(false);
  };

  const handleClaim = async () => {
    setBusy(true);
    try {
      const res = await api.post("/mining/claim");
      toast.success(res.data.message || `Claimed ${res.data.claimed_ltg} LTG`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to claim");
    }
    setBusy(false);
  };

  const handleTransfer = async () => {
    setBusy(true);
    try {
      const res = await api.post("/mining/transfer-to-wallet");
      toast.success(res.data.message || "Transferred to wallet");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer locked");
    }
    setBusy(false);
  };

  const handleInvite = async () => {
    if (!invitePhone.trim()) return toast.error("Enter friend's phone");
    setBusy(true);
    try {
      const res = await api.post("/mining/invite", { phone: invitePhone.trim() });
      toast.success(res.data.message);
      setInvitePhone("");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    }
    setBusy(false);
  };

  const handleAccept = async (id) => {
    setBusy(true);
    try {
      const res = await api.post(`/mining/invites/${id}/accept`);
      toast.success(res.data.message);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
    }
    setBusy(false);
  };

  const handleReject = async (id) => {
    setBusy(true);
    try {
      await api.post(`/mining/invites/${id}/reject`);
      toast.info("Invite rejected");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
    setBusy(false);
  };

  const handleRemind = async (memberId, name) => {
    setBusy(true);
    try {
      await api.post("/mining/remind", { member_id: memberId });
      toast.success(`Reminder sent to ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remind");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="screen-container flex items-center justify-center" data-testid="mine-loading">
        <Pickaxe className="animate-spin text-primary" size={42} />
      </div>
    );
  }

  const remainingHrs = Math.floor((status?.remaining_sec || 0) / 3600);
  const remainingMin = Math.floor(((status?.remaining_sec || 0) % 3600) / 60);
  const isActive = !!status?.active;
  const minedLockedBase = status?.mined_ltg_balance || 0;
  // Live locked-pool projection: show base + currently accruing (so both numbers tick together)
  const minedLockedLive = isActive ? minedLockedBase + displayAccrued : minedLockedBase;
  const acceptedTeamCount = (invites.members || []).length;

  return (
    <div className="screen-container pb-24" data-testid="mine-page">
      <div className="p-4 border-b border-border flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="p-2" data-testid="mine-back-btn">
          <ChevronLeft size={24} />
        </button>
        <Pickaxe size={24} className="text-primary" />
        <h1 className="font-bold text-xl">LTG Mining</h1>
      </div>

      <div className="p-4 space-y-4">
        {/* Big live counter */}
        <div className={`relative rounded-2xl p-6 text-center border-2 ${isActive ? "border-green-400/60 bg-gradient-to-b from-green-500/15 to-black" : "border-border bg-black/40"}`}>
          {isActive && (
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-green-300">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span> Mining Active
            </span>
          )}
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Currently Mining</p>
          <div className="text-5xl font-black text-primary tabular-nums" data-testid="mining-counter">
            {displayAccrued.toFixed(4)}
          </div>
          <p className="text-xs text-yellow-300/80 mt-1">Lite Gold</p>
          {isActive ? (
            <p className="text-[11px] text-muted-foreground mt-3">
              ⏳ {String(remainingHrs).padStart(2, "0")}:{String(remainingMin).padStart(2, "0")} hrs remaining
            </p>
          ) : (
            <p className="text-[11px] text-orange-300 mt-3">Session ended — Claim & Restart</p>
          )}
        </div>

        {/* Action button */}
        {!isActive ? (
          (status?.accrued_ltg || 0) > 0 ? (
            <button
              onClick={handleClaim}
              disabled={busy}
              className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-bold py-4 rounded-xl text-lg shadow-lg"
              data-testid="claim-mining-btn"
            >
              💰 Claim {(status?.accrued_ltg || 0).toFixed(4)} LTG
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={busy}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 disabled:opacity-50 text-white font-bold py-4 rounded-xl text-lg shadow-lg"
              data-testid="start-mining-btn"
            >
              ⛏️ Start 24h Mining
            </button>
          )
        ) : (
          <div className="bg-black/40 border border-border rounded-xl p-3 text-center text-xs text-muted-foreground">
            Mining will auto-stop in {remainingHrs}h {remainingMin}m. Come back to claim & restart.
          </div>
        )}

        {/* Rate breakdown */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-black/40 border border-border rounded-xl p-3" data-testid="rate-card">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-yellow-400" />
              <span className="text-[10px] uppercase text-muted-foreground tracking-wider">Your Rate</span>
            </div>
            <p className="text-xl font-bold text-primary">
              {(isActive ? status.session_rate_per_day : status.rate_per_day).toFixed(0)}
              <span className="text-xs text-muted-foreground"> /day</span>
            </p>
          </div>
          <div className="bg-black/40 border border-border rounded-xl p-3" data-testid="team-card">
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-blue-400" />
              <span className="text-[10px] uppercase text-muted-foreground tracking-wider">Active Team</span>
            </div>
            <p className="text-xl font-bold text-blue-300">
              {status.team_active_count}
              <span className="text-xs text-muted-foreground"> / {acceptedTeamCount} mining</span>
            </p>
          </div>
        </div>

        {/* === MINING TEAM SECTION === */}
        <div className="bg-gradient-to-r from-purple-500/15 to-blue-500/15 border border-purple-400/40 rounded-xl p-4 space-y-3" data-testid="mining-team-section">
          <div className="flex items-center gap-2">
            <UserPlus size={16} className="text-purple-300" />
            <h3 className="font-semibold text-sm">Build Your Mining Team</h3>
          </div>

          {/* Invite by phone */}
          <div className="flex gap-2">
            <input
              type="tel"
              placeholder="Friend's registered phone..."
              value={invitePhone}
              onChange={(e) => setInvitePhone(e.target.value)}
              className="flex-1 bg-black/50 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
              data-testid="invite-phone-input"
            />
            <button
              onClick={handleInvite}
              disabled={busy || !invitePhone.trim()}
              className="bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg text-sm"
              data-testid="invite-send-btn"
            >
              Invite
            </button>
          </div>

          {/* Incoming invites */}
          {invites.incoming?.length > 0 && (
            <div className="space-y-2 pt-1">
              <p className="text-[11px] uppercase tracking-wider text-yellow-300">📬 Incoming Invites ({invites.incoming.length})</p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scroll">
                {invites.incoming.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between bg-black/40 border border-yellow-400/40 rounded-lg p-2.5" data-testid={`incoming-invite-${inv.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{inv.from_name}</p>
                      <p className="text-[10px] text-muted-foreground">wants you in their mining team</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => handleAccept(inv.id)}
                        disabled={busy}
                        className="bg-green-500 hover:bg-green-400 text-white rounded-md p-1.5"
                        data-testid={`accept-invite-${inv.id}`}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => handleReject(inv.id)}
                        disabled={busy}
                        className="bg-red-500/70 hover:bg-red-500 text-white rounded-md p-1.5"
                        data-testid={`reject-invite-${inv.id}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* My team members */}
          {invites.members?.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <p className="text-[11px] uppercase tracking-wider text-blue-300">
                👥 Your Team ({invites.members.length})
                {invites.members.length > 5 && <span className="text-muted-foreground normal-case ml-2">— scroll to see all</span>}
              </p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1 custom-scroll" data-testid="team-members-scroll">
                {invites.members.map((m) => (
                  <div key={m.member_id} className="flex items-center justify-between bg-black/30 rounded-lg px-2.5 py-2 text-xs gap-2" data-testid={`team-member-${m.member_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{m.name || "Teammate"}</p>
                      {m.mining_active ? (
                        <span className="text-green-300 flex items-center gap-1 text-[10px]">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                          mining today &nbsp;•&nbsp; you both earn +{status.bonus_per_member}/day
                        </span>
                      ) : (
                        <span className="text-red-400 flex items-center gap-1 text-[10px]">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                          not mining today
                        </span>
                      )}
                    </div>
                    {!m.mining_active && (
                      <button
                        onClick={() => handleRemind(m.member_id, m.name)}
                        disabled={busy}
                        className="bg-orange-500 hover:bg-orange-400 text-white rounded-md px-2 py-1 text-[10px] font-bold flex items-center gap-1 shrink-0"
                        data-testid={`remind-${m.member_id}`}
                      >
                        <Bell size={10} /> Remind
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outgoing pending */}
          {invites.outgoing?.filter(i => i.status === 'pending').length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">⏳ Pending Sent ({invites.outgoing.filter(i => i.status === 'pending').length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto pr-1 custom-scroll">
                {invites.outgoing.filter(i => i.status === 'pending').map((inv) => (
                  <div key={inv.id} className="text-[11px] text-muted-foreground bg-black/20 rounded px-2 py-1" data-testid={`outgoing-invite-${inv.id}`}>
                    {inv.to_name || inv.to_phone} — waiting to accept
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Earnings explanation */}
        <div className="bg-gradient-to-r from-blue-500/15 to-cyan-500/15 border border-blue-400/40 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-blue-300" />
            <h3 className="font-semibold text-sm">How Earnings Work</h3>
          </div>
          <ul className="text-[12px] space-y-1.5 text-muted-foreground">
            <li>• Base rate: <span className="text-white font-bold">{status.base_rate_per_day} LTG/day</span></li>
            <li>• Each team member mining today: <span className="text-green-300 font-bold">+{status.bonus_per_member} LTG/day</span></li>
            <li>• Your bonus today: <span className="text-blue-300 font-bold">+{status.team_bonus_per_day.toFixed(0)} LTG/day</span></li>
            <li>• Counter ticks every second — both pools grow live</li>
          </ul>
        </div>

        {/* Mined balance + transfer (live ticking) */}
        <div className="bg-black/60 border border-yellow-400/40 rounded-xl p-4" data-testid="mined-balance-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Coins size={16} className="text-yellow-400" />
              <h3 className="font-semibold text-sm">Mined LTG (Locked Pool)</h3>
            </div>
            {!status.transfers_enabled && (
              <Lock size={14} className="text-orange-400" />
            )}
          </div>
          <p className="text-3xl font-black text-yellow-300 tabular-nums" data-testid="locked-pool-value">
            {minedLockedLive.toFixed(4)} <span className="text-sm text-muted-foreground">LTG</span>
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Lifetime mined: {(status.total_mined_ltg || 0).toFixed(2)} LTG
            {isActive && <span className="text-green-300 ml-2">• Live</span>}
          </p>
          <button
            onClick={handleTransfer}
            disabled={busy || !status.transfers_enabled || minedLockedBase <= 0}
            className={`w-full mt-3 py-2.5 rounded-lg font-bold text-sm transition ${
              status.transfers_enabled && minedLockedBase > 0
                ? "bg-yellow-400 hover:bg-yellow-300 text-black"
                : "bg-black/40 text-muted-foreground border border-border cursor-not-allowed"
            }`}
            data-testid="transfer-mining-btn"
          >
            {status.transfers_enabled
              ? `Transfer to Main Wallet`
              : "🔒 Transfers Locked Until Exchange Listing"}
          </button>
        </div>

        <div className="text-center text-[11px] text-muted-foreground px-2">
          💡 Bigger team → bigger daily mining. Each active miner adds {status.bonus_per_member} LTG/day to your rate.
        </div>
      </div>
    </div>
  );
};

export default MinePage;
